import { useState } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { DialogCard } from './DialogCard';
import { Dialog } from './Dialog';
import { proAccountThemeCoreUI as theme } from '../themes';

export type GenericDialogProps = {
  title: string;
  subtitle: string;
  navigationText: string;
  dialogTitle?: string;
  dialogSubtitle?: string;
  primaryButton: {
    text: string;
    link: string;
    isExternalLink: boolean;
  };
  styleOverrides?: {
    backgroundColor?: string;
  };
};

const defaultValues = {
  dialogTitle: 'You are being redirected',
  dialogSubtitle:
    'You’re about to leave this site and be redirected to a new page—click ‘Continue’ to proceed or close this window to stay here.',
  dialogButtonLabel: 'Continue',
};

/**
 * Props for configuring a generic dialog component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @property {string} title - The main title displayed on the card.
 * @property {string} subtitle - The text displayed in the card body.
 * @property {string} [navigationText] - The text displayed in the button label on the card.
 * @property {string} [dialogTitle] - Optional title displayed in the popup modal.
 * @property {string} [dialogSubtitle] - Optional subtitle displayed in the popup modal body.
 * @property {Object} primaryButton - Configuration for the primary button in the modal.
 * @property {string} primaryButton.text - The text displayed on the button.
 * @property {string} primaryButton.link - The URL the button links to.
 * @property {boolean} primaryButton.isExternalLink - Indicates whether the link opens in a new tab or window.
 * @property {Object} [styleOverrides] - Optional overrides for the dialog's styling.
 * @property {string} [styleOverrides.backgroundColor] - Custom background color for the dialog.
 */

export const GenericDialog = ({
  title,
  subtitle,
  navigationText,
  dialogTitle = defaultValues.dialogTitle,
  dialogSubtitle = defaultValues.dialogSubtitle,
  primaryButton,
  styleOverrides,
}: GenericDialogProps) => {
  const [isDialog, setDialog] = useState<boolean>(false);
  const { text, link, isExternalLink } = primaryButton ?? {};

  const handleRedirect = () => {
    try {
      if (!link) return;
      const url = new URL(link);
      window.location.href = url.href;
    } catch {
      console.error(`Invalid URL: ${link}`);
    }
  };

  const renderGenericDialogContent = (
    <DialogContent
      sx={{ padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}` }}
    >
      <DialogActions sx={{ display: 'flex', gap: theme.spacing(2) }}>
        <Button
          data-testid="generic-dialog-close-button"
          onClick={() => setDialog(false)}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          data-testid="generic-dialog-button-2"
          component="a"
          variant="contained"
          target="_blank"
          disabled={!link}
          onClick={() => handleRedirect()}
        >
          {text}{' '}
          <OpenInNewIcon
            sx={{
              marginLeft: theme.spacing(0.5),
            }}
          />
        </Button>
      </DialogActions>
    </DialogContent>
  );

  return (
    <>
      <DialogCard
        id="dialog-card"
        onClick={isExternalLink ? () => setDialog(true) : undefined}
        href={!isExternalLink ? link : undefined}
        isHighlighted
        isExternalLink={isExternalLink}
        cardTitle={title}
        cardSubtitle={subtitle}
        cardButtonLabel={isExternalLink ? navigationText : text}
        customBackground={styleOverrides?.backgroundColor ?? ''}
      />

      {isDialog && (
        <Dialog
          onClose={() => setDialog(false)}
          open={isDialog}
          subtitle={dialogSubtitle}
          title={dialogTitle}
          maxWidth="sm"
          handleClose={() => setDialog(false)}
        >
          {renderGenericDialogContent}
        </Dialog>
      )}
    </>
  );
};
